import moment from 'moment';
import { i18n } from '../../../../main';
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';
import mapConfig from '@/pages/mapView/components/map/map.config';
import vuex from '@/store';
import { Device, MetaDeviceBase, FloodSensorDevice } from '@/modules/Types';
import router from '@/router';
import { RETROFIT_STATE, PARTIAL_RETROFIT_STATE } from '@/modules/retrofitActions';

export interface CabinetTime {
  on_time: string;
  off_time: string;
}

// The tinted backgrounds are pale enough to melt into the surface behind them, so each badge
// is outlined in a mid tone of its own hue to keep its shape readable.
const RETROFIT_BADGE_COLORS = { background: '#FDF1E1', text: '#E8871E', border: '#F3CE9B' };
// Purple of the virtual cabinet map icon (mapView_icons/virtual_cabinet), darkened for the
// text so it stays readable on the tinted background.
const VIRTUAL_BADGE_COLORS = { background: '#F5E9FE', text: '#7E2FC4', border: '#D9B8F5' };

class DeviceFactory {
  id: string;
  name: string;
  category: string;
  metaDevice: MetaDeviceBase;
  sysConnected: string;
  address: string;
  estimated_address: boolean;
  dmodified: string;
  sys_online: string;
  online: string;
  fault: string;
  metaType: string;
  device: Device;
  cabinetTime: CabinetTime;
  astronomicalClock: boolean;
  part_number: string;
  text_style: string;
  div_style: string;
  clock_style: string;
  cabinetOperationWindow: string | null;
  disconnect_reason: string;

  constructor(device: Device) {
    this.id = device.id;
    this.name =
      device.class_name === 'CONTAINER'
        ? `container ${device.container_id}`
        : device.name;
    const category = device['meta.category']?.category || '';
    this.category = category ? category.toLocaleLowerCase() : '';
    this.fault = device.fault > 0 ? 'Fault' : 'OK';
    this.online = device.sys___active === 0 ? 'Disconnected' : device.sys___active === 1 ? 'Connected' : 'N/A';
    this.metaType = device['meta.type.type'];
    this.dmodified = this.getTime(device.dmodified_ts * 1000);
    this.device = device;
    this.metaDevice = device['meta.device'];
    this.sysConnected =
      device.sys___connected === 0
        ? i18n.t('Disconnected')
        : device.sys___connected === 1
        ? i18n.t('Connected')
        : i18n.t('N/A');
    const device_address = this.metaDevice[Utils.hasCabinetClass(device['meta.category'].category) ? 'cabinet_address' : 'address'];
    if (!device_address) {
      this.address = this.metaDevice.estimated_address || 'N/A';
      this.estimated_address = this.address !== 'N/A';
    } else {
      this.address = device_address;
      this.estimated_address = false;
    }
    let cabinet_next_activation = (vuex.state as any).ProjectStreams.next_activation_by_cabinet.get(this.device.id);
    cabinet_next_activation = cabinet_next_activation && cabinet_next_activation.slice(-1)[0][1];
    this.cabinetTime = cabinet_next_activation || { on_time: '', off_time: '' };
    this.astronomicalClock = Utils.hasTondoClass(this.device['meta.category'].category) && PropUtils.hasAstronomicalClock(device.id);
    this.part_number = device['meta.part_number.part_number'];
    this.text_style = 'font-weight: 850; margin-top: 2px';
    this.clock_style = 'margin-top: 2px;';
    this.div_style = 'padding-right: 10px; padding-left: 10px; flex-wrap: wrap; font-family: Open Sans, Arial, monospace !important;';
    this.cabinetOperationWindow = device['meta.category'].category === 'HUB' ? Utils.getCabinetOperationWindow(device, (vuex.state as any).Global.timezone) : null;
    this.disconnect_reason = Utils.getDisconnectReason(this.device.disconnect_reason);
  }

  getRetrofitStatusLabel() {
    const operation_state = Utils.getOperationState(this.device);

    if (operation_state === RETROFIT_STATE) {
      return i18n.t('Retrofit') as string;
    }
    if (operation_state === PARTIAL_RETROFIT_STATE) {
      return i18n.t('Partial Retrofit') as string;
    }
    if (Utils.isPendingVerification(this.device)) {
      return i18n.t('Pending Verification') as string;
    }

    return '';
  }

  hasRetrofitStatus() {
    return !!this.getRetrofitStatusLabel();
  }

  // Rendered as the popup's first content line. It can't live on the close
  // button's row (InfoWindow headerContent), because that row sits outside the
  // popup card's background and gets clipped by its edges.
  // `outside_content_div` adds the popup's own horizontal padding for callers
  // that render this line before opening the padded content div, so the badge
  // stays aligned with the text below it.
  getRetrofitStatusLine(outside_content_div = false) {
    const label = this.getRetrofitStatusLabel();
    if (!label) return '';

    const padding = outside_content_div ? 'padding-right: 10px; padding-left: 10px;' : '';
    const is_virtual_cabinet = this.device.class_name === 'virtual_cabinet' || Utils.hasVirtualCabinetClass(this.device['meta.category']);
    const virtual_badge = is_virtual_cabinet ? this.getVirtualBadge() : '';

    return `<div style="text-align: start; margin-bottom: 4px; ${padding}">${this.getBadgeHtml(label, RETROFIT_BADGE_COLORS)}${virtual_badge}</div>`;
  }

  // The popup is handed to the Google Maps InfoWindow as an HTML string, so the badges
  // cannot use the stylesheet - `.cabinet-title-chip` in styles.scss mirrors these values
  // to give the cabinet page the same badges.
  getBadgeHtml(label, colors, extra_style = '') {
    return `<span style="display:inline-block; background-color:${colors.background}; color:${colors.text}; border:1px solid ${colors.border}; font-weight:700; font-size:11px; padding:2px 8px; border-radius:999px; ${extra_style}">${label}</span>`;
  }

  // Standalone "Virtual" badge — shown next to the Retrofit/Partial Retrofit
  // badge for virtual cabinets, and on its own for virtual controllers (which
  // aren't real hardware, so a Retrofit state never applies to them).
  getVirtualBadge() {
    const lang = (vuex.state as any).Global.lang;
    const margin = lang === 'en' ? 'margin-left: 6px;' : 'margin-right: 6px;';
    return this.getBadgeHtml(i18n.t('Virtual'), VIRTUAL_BADGE_COLORS, margin);
  }

  getVirtualBadgeLine(outside_content_div = false) {
    const padding = outside_content_div ? 'padding-right: 10px; padding-left: 10px;' : '';
    return `<div style="text-align: start; margin-bottom: 4px; ${padding}">${this.getBadgeHtml(i18n.t('Virtual'), VIRTUAL_BADGE_COLORS)}</div>`;
  }

  getTime(time) {
    if (time) {
      return moment(time).format('L') + ' ' + moment(time).format('hh:mm:ss');
    } else {
      return '';
    }
  }

  getPopUpText() {
    switch (this.category) {
      case 'fixture':
        return this.device.class_name === 'virtual_controller' ? this.getVirtualControllerInformation() : this.getFixtureInformation();
      case 'motion sensor':
        return this.getMotionSensorInformation();
      case 'flood sensor':
        return this.getFloodSensorInformation();
      case 'barrier':
        return this.getFloodSensorInformation();
      case 'video':
        return this.getVideoInformation();
      case 'cabinet':
        return this.getCabinetInformation();
      case 'hub':
        return this.getCabinetInformation();
      case 'water meter':
        return this.getWaterMeterInformation();
      case 'container':
        return this.getContainerInformation();
      default:
        return this.getCommonProperties() + ' </div>';
    }
  }

  getWaterMeterInformation(){
    const openDiv = `<div style="${this.div_style}">`;
    const id = `<b style="${this.text_style}">${i18n.t('ID')}: </b> ${this.id}<br/>`;
    const status = `<b style="${this.text_style}">${i18n.t('Status')}: </b> ${this.sysConnected}<br/>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}:</b> ${
      this.address
    } ${this.estimated_address ? `(${i18n.t('estimated')})` : ''} <br/>`;
    const name = `<span><b style="${this.text_style}">${i18n.t('Name')}:</b> ${
      this.name
    }</span><div>`;
    const closeDiv = '</div>';
    return openDiv + id + status + address + name + closeDiv;
  }

  getContainerInformation() {
    const openDiv = `<div style="${this.div_style}">`;
    const container_id = `<b style="${this.text_style}">${i18n.t(
      'Container ID'
    )}: </b> ${this.device.container_id}<br/>`;
    const poleNumber = `<b style="${this.text_style}">${i18n.t(
      'Pole Number'
    )}: </b> ${
      this.metaDevice ? this.metaDevice.pole_number || 'N/A' : 'N/A'
    }<br/>`;
    const cabinetId = `<b style="${this.text_style}">${i18n.t(
      'Cabinet ID'
    )}: </b> ${
      this.metaDevice ? this.metaDevice.cabinet_id || 'N/A' : 'N/A'
    }<br/>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}: </b> ${
      this.address
    }<br/>`;
    const closeDiv = '</div>';
    return openDiv + container_id + poleNumber + cabinetId + address + closeDiv;
  }

  getCommonProperties() {
    let text;
    const openDiv = `<div style="${this.div_style}">`;
    const name = `<span><b style="${this.text_style}">${i18n.t('Name')}:</b> ${
      this.name
    }</span><div>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}:</b> ${
      this.address
    } ${this.estimated_address ? `(${i18n.t('estimated')})` : ''} <br/>`;

    text = openDiv + address + name;

    return text;
  }

  checkCoord(coord) {
    if (!coord && coord === '0.0') {
      return;
    }
    if (typeof coord === 'string') {
      return Number(coord).toFixed(3);
    } else if (typeof coord === 'number') {
      return coord.toFixed(3);
    } else {
      return coord;
    }
  }

  getFixtureInformation() {
    let text = '';
    const lang = (vuex.state as any).Global.lang;
    const inCabinetPage = router.currentRoute.path.includes('cabinet-details');
    const alertFilterOn = this.filterType === 'alerts';
    const openDiv = `<div style="${this.div_style}">`;
    text = text + openDiv + this.getRetrofitStatusLine();
    const cabinetNumber = `<b style="${this.text_style}">${i18n.t(
      'Cabinet ID'
    )}: </b> ${
      this.metaDevice ? this.metaDevice.cabinet_id || i18n.t('N/A') : i18n.t('N/A')
    }<br/>`;
    text = text + cabinetNumber;
    const circuitNumber =
      this.metaDevice && this.metaDevice.circuit_number
        ? `<b style="${this.text_style}">${i18n.t('Circuit Number')}: </b> ${
            this.metaDevice.circuit_number
          }<br/>`
        : '';
    if (circuitNumber) text = text + circuitNumber;

    const poleNumber = `<b style="${this.text_style}">${i18n.t(
      'Pole Number'
    )}: </b> ${
      this.metaDevice ? this.metaDevice.pole_number || i18n.t('N/A') : i18n.t('N/A')
    }<br/>`;

    const fixtureIcon = `<b style="${this.text_style}">${i18n.t(
      'Fixture Icon'
    )}: </b> ${
      this.metaDevice
        ? i18n.t(this.metaDevice.fixture_icon) || i18n.t('Regular')
        : i18n.t('Regular')
    }<br/>`;

    const part_number = this.part_number
      ? `<b style="${this.text_style}">${i18n.t('Part Number')}: </b> ${
          this.part_number
        }<br/>`
      : '';
    const status = this.hasRetrofitStatus() ? '' : `<b style="${this.text_style}">${i18n.t('Status')}: </b> ${
      this.sysConnected
    }<br/>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}: </b> ${
      this.address
    } ${this.estimated_address ? `(${i18n.t('estimated')})` : ''}<br/>`;
    const name = `<b style="${this.text_style}">${i18n.t('Name')}: </b> ${
      this.name
    }<br/>`;
    let scan_number = '';
    if (this.metaDevice.scan_numbering) {
      scan_number = `<b style="${this.text_style}">${i18n.t(
        'Scan Number'
      )}: </b>`;
      if (this.metaDevice.total_scan_number) {
        scan_number =
          scan_number +
          `${this.metaDevice.scan_numbering} ${i18n.t('out of')} ${
            this.metaDevice.total_scan_number
          }<br/>`;
      } else {
        scan_number = scan_number + `${this.metaDevice.scan_numbering}<br/>`;
      }
    }

    const alert_type = inCabinetPage && alertFilterOn ? `<b style="${this.text_style}">${i18n.t(
      'Alert Type'
    )}: </b> ${this.disconnect_reason}<br/>` : '';
    let img,
      clock_text = '',
      isAstronomical = '',
      clockIcon = '';
    if (!this.hasRetrofitStatus()) {
      if (this.astronomicalClock) {
        img = mapConfig.icons['astronomicalClock'];
        clock_text = `${i18n.t('Yes')}`;
      } else {
        img = mapConfig.icons['noAstronomicalClock'];
        clock_text = `${i18n.t('No')}`;
      }
      isAstronomical = `<div class="d-flex"><b style="${
        this.text_style
      }">${i18n.t('Astronomical Clock')}: </b><div style="${this.clock_style}${
        lang === 'en' ? 'margin-left: 3px;' : 'margin-right: 3px;'
      }">${clock_text}</div>`;
      clockIcon = `<img width="20" height="20" style="${
        lang === 'en' ? 'margin-left: 10px;' : 'margin-right: 8px;'
      }" src="${img}"/></div>`;
    }
    const closeDiv = '</div>';
    // const clockIcon = `<div style='position: absolute; top: 0px; left: 120px;'>${this.astronomicalClock ? `<i style='color: #2196f3;' class='material-icons'>check</i>` : `<i class='material-icons' style='color: #f44336;'>close</i>`}</div></div><br/>`;
    text =
      text +
      poleNumber +
      part_number +
      status +
      address +
      name +
      fixtureIcon +
      scan_number +
      isAstronomical +
      clockIcon +
      alert_type +
      closeDiv;
    return text;
  }

  getMotionSensorInformation() {
    const openDiv = `<div style="${this.div_style}">`;
    const closeDiv = '</div>';

    const name = `<b style="${this.text_style}">${i18n.t('Name')}:</b> ${
      this.name
    }<br/>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}:</b> ${
      this.address
    } ${this.estimated_address ? `(${i18n.t('estimated')})` : ''} <br/>`;
    return openDiv + name + address + closeDiv;
  }

  getFloodSensorInformation() {
    const openDiv = `<div style="${this.div_style}">`;
    const closeDiv = '</div>';

    const name = `<b style="${this.text_style}">${i18n.t('Name')}:</b> ${
      this.name
    }<br/>`;
    const address = `<b style="${this.text_style}">${i18n.t('Address')}:</b> ${
      this.address
    } ${this.estimated_address ? `(${i18n.t('estimated')})` : ''} <br/>`;
    const floodDevice = this.device as FloodSensorDevice;
    let height = this.metaDevice.hasOwnProperty('sensor_height') && this.metaDevice.sensor_height !== '' && floodDevice.water_mark !== null
      ? (+this.metaDevice.sensor_height - +floodDevice.water_mark) / 10
      : null;
    height = height && height < 0 ? 0 : height;
    const water_height = `<b style="${this.text_style}">${i18n.t('Water Level')}:</b> ${
      height >= 0 ? `${height} ${i18n.t('cm')}` : i18n.t('N/A')
    }<br/>`;

    const bottom_height = this.metaDevice.hasOwnProperty('bottom_float_height') && this.metaDevice.bottom_float_height !== '' 
      ? `${this.metaDevice.bottom_float_height} ${i18n.t('cm')}` 
      : i18n.t('N/A');
    const top_height = this.metaDevice.hasOwnProperty('top_float_height') && this.metaDevice.top_float_height !== '' 
      ? `${this.metaDevice.top_float_height} ${i18n.t('cm')}` 
      : i18n.t('N/A');
    const bottom_status = +floodDevice.lsi1 === 1 ? `${i18n.t('Wet')}` : `${i18n.t('Dry')}`;
    const top_status = +floodDevice.lsi2 === 1 ? `${i18n.t('Wet')}` : `${i18n.t('Dry')}`;
    const bottom_float = `<b style="${this.text_style}">${i18n.t('Bottom Float')} ${bottom_height === 'N/A' ? '' : `${i18n.t('at')} ${bottom_height}`}:</b> ${
      bottom_status
    }<br/>`;
    const top_float = `<b style="${this.text_style}">${i18n.t('Top Float')} ${top_height === 'N/A' ? '' : `${i18n.t('at')} ${top_height}`}:</b> ${
      top_status
    }<br/>`;
    return openDiv + 
      name + 
      address + 
      water_height + 
      bottom_float + 
      top_float + 
      closeDiv;
  }


  getVirtualControllerInformation() {
    let text;
    const type = this.metaType
      ? `<b style="${this.text_style}">${i18n.t('Type')}:</b> ${
          this.metaType
        }<br/>`
      : '';
    const cabinetNumber = `<b style="${this.text_style}">${i18n.t(
        'Cabinet ID'
      )}: </b> ${
        this.metaDevice ? this.metaDevice.cabinet_id || i18n.t('N/A') : i18n.t('N/A')
      }<br/>`;
    const poleNumber = `<b style="${this.text_style}">${i18n.t(
        'Pole Number'
      )}: </b> ${
        this.metaDevice ? this.metaDevice.pole_number || i18n.t('N/A') : i18n.t('N/A')
      }<br/>`;
    const circuitNumber =
      this.metaDevice && this.metaDevice.circuit_number
        ? `<b style="${this.text_style}">${i18n.t('Circuit Number')}: </b> ${
            this.metaDevice.circuit_number
          }<br/>`
        : '';
    text = this.getVirtualBadgeLine(true) + this.getCommonProperties() + cabinetNumber + poleNumber + circuitNumber + type + ' </div>';
    return text;
  }

  getCabinetInformation() {
    let moreInfo = '';
    if (this.cabinetTime.on_time !== '' && this.cabinetTime.off_time !== '') {
      const onTime = `<b style="${this.text_style}">${i18n.t('On Time')}:</b> ${
        this.cabinetTime.on_time
      }<br/>`;
      const offTime = `<b style="${this.text_style}">${i18n.t(
        'Off Time'
      )}:</b> ${this.cabinetTime.off_time}<br/>`;
      moreInfo = onTime + offTime;
    }
    const cabinetNumber = `<span style="padding: 0 10px; font-family: 'Open Sans', 'Arial', monospace !important;"><b style="${
      this.text_style
    }">${i18n.t('Cabinet ID')}: </b> ${
      this.metaDevice ? this.metaDevice.cabinet_id || i18n.t('N/A') : i18n.t('N/A')
    }</span><br/>`;
    const operationWindow = `<span style="padding: 0 10px; font-family: 'Open Sans', 'Arial', monospace !important;"><b style="${
      this.text_style
    }">${i18n.t('Operation Window')}: </b> ${
      this.cabinetOperationWindow
    }</span><br/>`;
    let status = '';
    if (this.device.class_name !== 'virtual_cabinet' && !this.hasRetrofitStatus()) {
      const title = this.fault
      ? `<span style="padding: 0 10px; font-family: 'Open Sans', 'Arial', monospace !important;"><b style="${
        this.text_style
      }">${i18n.t('Status')}:</b> `
      : '';
      const state = `${
        this.online === 'Connected' ? i18n.t('Connected') : (this.online === 'Disconnected' ? i18n.t('Disconnected') : i18n.t('N/A'))
      } `;
      const time = this.online !== 'N/A' ? `${this.dmodified}</span><br/>` : '</span><br/>';
      status = title + state + time;
    }
    const popupText =
      this.getRetrofitStatusLine(true) + cabinetNumber + operationWindow + status + this.getCommonProperties() + moreInfo + ' </div>';
    return popupText;
  }

  getVideoInformation() {
    let text;
    text = this.getCommonProperties() + '</div>';
    return text;
  }

  get filterType(): string {
    return (vuex.state as any).MapSettings.filterType;
  }
}

export function createDevice(device: any) {
  return new DeviceFactory(device);
}
